<script setup>
/* global axios */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, reactive, watch, onBeforeUnmount } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SmmProgress from './SmmProgress.vue';
import SmmGroupGrowth from './SmmGroupGrowth.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
const props = defineProps({
  accountId: { type: String, required: true },
  group: { type: Object, required: true },
  disabled: Boolean,
});
const dialog = ref(null);
const opened = ref(false);
const loading = ref(false);
const busy = ref(false);
const denied = ref(false);
const error = ref('');
const notice = ref('');
const data = ref(null);
const quote = ref(null);
const confirmed = ref(false);
const uncertain = ref('');
const search = ref('');
const resolving = ref(null);
const form = reactive({
  service: '',
  quantity: '',
  target: '',
  targetConfirmed: false,
});
const resolution = reactive({ number: '', reason: '', absent: false });
let generation = 0;
let requests = new AbortController();
let pendingQuote = null;
let pendingResolution = null;
let purchaseId = null;
const api = () =>
  `/api/v1/accounts/${props.accountId}/tg_finance/smm_purchases`;
const catalogue = computed(() => data.value?.catalogue?.items || []);
const matches = computed(() =>
  catalogue.value.filter(s =>
    `${s.name} ${s.category}`
      .toLocaleLowerCase()
      .includes(search.value.toLocaleLowerCase())
  )
);
const rateMoney = v =>
  `${Number(v).toLocaleString('ru-RU', { maximumSignificantDigits: 12 })} ₽`;
const options = computed(() =>
  matches.value.slice(0, 100).map(s => ({
    value: s.id,
    label: `${s.name} · ${rateMoney(s.rate)}/шт. · ${s.min}–${s.max}`,
  }))
);
const selected = computed(() =>
  catalogue.value.find(s => s.id === form.service)
);

const money = v =>
  `${Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 6 })} ₽`;
const states = {
  quoted: 'Рассчитан',
  checking: 'Проверка перед отправкой',
  sending: 'Ожидается ответ сервиса',
  submitted: 'Заказ создан',
  unknown: 'Результат неизвестен — нужна сверка',
  failed: 'Не отправлен',
  not_created: 'По сверке: не создан',
};
const messages = {
  smm_not_configured: 'Подключите SexySMM в разделе «Учёт и аналитика».',
  smm_catalogue_wait:
    'Каталог недавно проверялся. Подождите 30 секунд и обновите.',
  smm_service_unavailable: 'Услуга недоступна. Обновите каталог.',
  smm_quantity_invalid: 'Количество выходит за ограничения услуги.',
  smm_target_invalid:
    'Укажите корректную ссылку https://t.me/… без дополнительных параметров.',
  smm_target_group_mismatch:
    'Ссылка должна относиться к выбранной группе. Для приглашения подтвердите принадлежность.',
  smm_quote_expired: 'Расчёт устарел. Рассчитайте стоимость ещё раз.',
  smm_price_changed: 'Цена увеличилась. Заказ не отправлен — обновите расчёт.',
  smm_insufficient_balance: 'Недостаточно средств SexySMM. Заказ не отправлен.',
  smm_connection_changed: 'Подключение изменилось. Откройте оформление заново.',
  smm_purchase_in_progress:
    'Другой заказ ещё отправляется. Проверьте его состояние.',
  smm_unresolved_group:
    'По этой группе есть заказ с неизвестным результатом. Сначала сверьте его.',
  smm_duplicate_purchase:
    'Такой заказ только что создан. Проверьте историю; повтор доступен через две минуты.',
  smm_duplicate_quote:
    'Этот расчёт заменён другим подтверждением. Проверьте историю перед новым заказом.',
  smm_quote_wait: 'Подождите несколько секунд перед новым расчётом.',
  smm_preflight_failed:
    'Проверка перед отправкой не завершилась. Платный запрос не отправлен.',
  smm_purchase_unknown:
    'Сервис мог принять заказ. Сверьте кабинет SexySMM; повторной покупки автоматически не будет.',
};
const failure = e =>
  e?.response?.status === 403
    ? 'Доступ разрешён только администратору.'
    : messages[e?.response?.data?.error] ||
      'Не удалось выполнить запрос. Проверьте состояние заказа перед повтором.';
const request = (url, method = 'get', payload = null) =>
  axios({
    url,
    method,
    data: payload,
    signal: requests.signal,
    timeout: 45000,
  });
function resetForm() {
  pendingQuote = null;
  quote.value = null;
  confirmed.value = false;
  uncertain.value = '';
  search.value = '';
  Object.assign(form, {
    service: '',
    quantity: '',
    target: props.group.username ? `https://t.me/${props.group.username}` : '',
    targetConfirmed: false,
  });
}
async function load() {
  const current = generation;
  loading.value = true;
  try {
    const response = await request(`${api()}/groups/${props.group.id}`);
    if (current !== generation) return;
    data.value = response.data;
    denied.value = false;
  } catch (e) {
    if (current === generation) {
      denied.value = e?.response?.status === 403;
      error.value = failure(e);
    }
  } finally {
    if (current === generation) loading.value = false;
  }
}
async function refreshCatalogue() {
  if (busy.value || denied.value) return;
  const current = generation;
  busy.value = true;
  error.value = '';
  try {
    const response = await request(`${api()}/catalogue/refresh`, 'post', {});
    if (current === generation && data.value)
      data.value.catalogue = response.data;
  } catch (e) {
    if (current === generation) error.value = failure(e);
  } finally {
    if (current === generation) busy.value = false;
  }
}
async function open() {
  generation += 1;
  requests.abort();
  requests = new AbortController();
  opened.value = true;
  error.value = '';
  notice.value = '';
  denied.value = false;
  data.value = null;
  resolving.value = null;
  purchaseId = null;
  resetForm();
  dialog.value?.open();
  await load();
  if (
    opened.value &&
    data.value?.catalogue?.configured &&
    data.value.catalogue.stale
  )
    await refreshCatalogue();
}
function close() {
  opened.value = false;
  generation += 1;
  requests.abort();
  requests = new AbortController();
  busy.value = false;
  loading.value = false;
  pendingQuote = null;
  pendingResolution = null;
  quote.value = null;
  form.target = '';
}
watch(
  () => [form.service, form.quantity, form.target, form.targetConfirmed],
  () => {
    if (!busy.value && !uncertain.value) {
      quote.value = null;
      confirmed.value = false;
      pendingQuote = null;
    }
  }
);
watch(
  () => [props.accountId, props.group.id],
  () => {
    close();
    data.value = null;
    dialog.value?.close();
  }
);
async function calculate() {
  if (busy.value || denied.value) return;
  if (!pendingQuote) {
    if (!selected.value) {
      error.value = 'Выберите услугу.';
      return;
    }
    if (!/^[1-9]\d{0,7}$/.test(form.quantity)) {
      error.value = messages.smm_quantity_invalid;
      return;
    }
    pendingQuote = {
      request_key: crypto.randomUUID(),
      group_entry_id: props.group.id,
      service_id: form.service,
      quantity: Number(form.quantity),
      target_url: form.target,
      target_confirmed: form.targetConfirmed,
    };
  }
  const current = generation;
  busy.value = true;
  error.value = '';
  notice.value = '';
  try {
    const response = await request(`${api()}/quote`, 'post', pendingQuote);
    if (current !== generation) return;
    quote.value = response.data;
    purchaseId = response.data.id;
    pendingQuote = null;
    uncertain.value = '';
  } catch (e) {
    if (current === generation) {
      error.value = failure(e);
      uncertain.value = !e?.response || e.response.status >= 500 ? 'quote' : '';
      if (!uncertain.value) pendingQuote = null;
    }
  } finally {
    if (current === generation) busy.value = false;
  }
}
function applyStatus(row) {
  if (row.state === 'quoted') {
    quote.value = row;
    uncertain.value = '';
    notice.value =
      'Заказ ещё не отправлен. Проверьте расчёт перед подтверждением.';
  } else if (row.state === 'submitted') {
    quote.value = null;
    uncertain.value = '';
    confirmed.value = false;
    notice.value = `Заказ №${row.provider_order_id} создан и передан в учёт. Фактическая стоимость будет проверена автоматически.`;
  } else if (row.state === 'not_created') {
    quote.value = null;
    uncertain.value = '';
    confirmed.value = false;
    notice.value =
      'По сверке заказ не создан. При необходимости рассчитайте новый.';
  } else if (row.state === 'failed') {
    quote.value = null;
    uncertain.value = '';
    confirmed.value = false;
    error.value = messages[row.error_code] || messages.smm_preflight_failed;
  } else if (row.state === 'unknown') {
    quote.value = null;
    uncertain.value = '';
    confirmed.value = false;
    error.value = messages.smm_purchase_unknown;
  } else {
    uncertain.value = 'dispatch';
    notice.value =
      'Ожидается результат. Проверка состояния не создаёт новый заказ.';
  }
}
async function dispatch() {
  if (busy.value || !quote.value || !confirmed.value || uncertain.value) return;
  const current = generation;
  busy.value = true;
  error.value = '';
  purchaseId = quote.value.id;
  try {
    const response = await request(`${api()}/${purchaseId}/dispatch`, 'post', {
      confirmed: true,
    });
    if (current !== generation) return;
    applyStatus(response.data);
    await load();
  } catch (e) {
    if (current === generation) {
      error.value = failure(e);
      if (e?.response && e.response.status >= 400 && e.response.status < 500) {
        quote.value = null;
        confirmed.value = false;
        uncertain.value = '';
        denied.value = e.response.status === 403;
      } else {
        uncertain.value = 'dispatch';
      }
    }
  } finally {
    if (current === generation) busy.value = false;
  }
}
async function checkStatus() {
  if (busy.value || !purchaseId) return;
  const current = generation;
  busy.value = true;
  error.value = '';
  try {
    const response = await request(`${api()}/${purchaseId}`);
    if (current !== generation) return;
    applyStatus(response.data);
    await load();
  } catch (e) {
    if (current === generation) error.value = failure(e);
  } finally {
    if (current === generation) busy.value = false;
  }
}
function beginResolution(row) {
  resolving.value = row;
  Object.assign(resolution, { number: '', reason: '', absent: false });
  pendingResolution = null;
  error.value = '';
}
async function resolve() {
  if (busy.value || !resolving.value) return;
  if (!pendingResolution) {
    if (
      !resolution.reason.trim() ||
      (!resolution.absent && !/^[1-9]\d{0,23}$/.test(resolution.number)) ||
      (resolution.absent && resolution.number)
    ) {
      error.value =
        'Укажите результат сверки, причину и либо номер, либо отсутствие заказа.';
      return;
    }
    pendingResolution = {
      request_key: crypto.randomUUID(),
      reason: resolution.reason,
      not_created: resolution.absent,
      provider_order_id: resolution.number || null,
    };
  }
  const current = generation;
  busy.value = true;
  error.value = '';
  try {
    await request(
      `${api()}/${resolving.value.id}/resolve`,
      'post',
      pendingResolution
    );
    if (current !== generation) return;
    resolving.value = null;
    pendingResolution = null;
    uncertain.value = '';
    notice.value = 'Результат сверки сохранён.';
    await load();
  } catch (e) {
    if (current === generation) {
      error.value = failure(e);
      uncertain.value =
        !e?.response || e.response.status >= 500 ? 'resolve' : '';
      if (!uncertain.value) pendingResolution = null;
    }
  } finally {
    if (current === generation) busy.value = false;
  }
}
onBeforeUnmount(() => {
  generation += 1;
  requests.abort();
  form.target = '';
});
</script>

<template>
  <Button
    label="Заказать SexySMM"
    slate
    ghost
    size="sm"
    :disabled="disabled"
    :aria-label="`Заказать SexySMM: ${group.title}`"
    @click="open"
  />
  <Dialog
    ref="dialog"
    title="Заказ SexySMM"
    :description="group.title"
    overflow-y-auto
    width="lg"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="close"
  >
    <p class="text-sm text-n-slate-11 m-0 break-words">
      Группа: {{ group.title }} · {{ group.chat_id }}
    </p>
    <Banner v-if="loading" color="blue" role="status">
      Загрузка услуг и истории…
    </Banner>
    <Banner v-if="error" color="ruby" role="alert">{{ error }}</Banner>
    <Banner v-if="notice" color="green" role="status">{{ notice }}</Banner>
    <template v-if="data && !denied">
      <Banner v-if="!data.catalogue.configured" color="amber">
        Сначала подключите SexySMM в «Учёте и аналитике».
      </Banner>
      <template v-else-if="!resolving">
        <fieldset
          :disabled="busy || Boolean(uncertain)"
          class="flex flex-col gap-4 min-w-0 border-0 p-0 m-0"
        >
          <Input v-model="search" label="Найти услугу Telegram" />
          <ComboBox
            v-model="form.service"
            :options="options"
            placeholder="Выберите услугу"
            aria-label="Услуга SexySMM"
          />
          <p v-if="!matches.length" class="text-sm text-n-slate-11 m-0">
            Подходящих услуг нет. Измените поиск или обновите каталог.
          </p>
          <p
            v-else-if="matches.length > 100"
            class="text-sm text-n-slate-11 m-0"
          >
            Показаны первые 100 услуг. Уточните поиск.
          </p>
          <Input
            v-model="form.quantity"
            label="Количество"
            inputmode="numeric"
          />
          <p v-if="selected" class="text-sm text-n-slate-11 m-0">
            От {{ selected.min }} до {{ selected.max }}. Тариф:
            {{ rateMoney(selected.rate) }} за одно выполнение.
          </p>
          <Input
            v-model="form.target"
            label="Ссылка на группу или её публикацию"
            autocomplete="off"
            placeholder="https://t.me/…"
          />
          <p class="text-sm text-n-slate-11 m-0">
            Для просмотров и реакций укажите публикацию этой группы. Для
            закрытой группы можно указать приглашение, подтвердив его
            принадлежность.
          </p>
          <label class="flex gap-2 items-start text-sm text-n-slate-12"
            ><input
              v-model="form.targetConfirmed"
              type="checkbox"
            />Подтверждаю, что приглашение ведёт в выбранную группу</label
          >
        </fieldset>
        <Button
          label="Обновить каталог"
          slate
          ghost
          :disabled="busy || Boolean(uncertain)"
          @click="refreshCatalogue"
        />
        <section
          v-if="quote"
          class="border border-n-weak rounded-xl p-4 flex flex-col gap-3 min-w-0"
        >
          <p class="text-sm text-n-slate-12 m-0 break-words">
            {{ quote.service_name }} · {{ quote.quantity }} шт.
          </p>
          <p class="text-lg font-medium text-n-slate-12 m-0">
            К оплате по текущему тарифу: {{ money(quote.quoted_cost) }}
          </p>
          <p class="text-sm text-n-slate-11 m-0">
            Расчёт действует 3 минуты. Перед отправкой проверяются цена и
            баланс. Итоговая стоимость сверяется с сервисом; оценка не
            записывается как расход.
          </p>
          <label class="flex gap-2 items-start text-sm text-n-slate-12"
            ><input
              v-model="confirmed"
              type="checkbox"
              :disabled="busy || Boolean(uncertain)"
            />Подтверждаю платный заказ для {{ group.title }} на
            {{ money(quote.quoted_cost) }} по текущему тарифу</label
          >
        </section>
      </template>
      <template v-if="resolving">
        <Banner color="amber">
          Сначала проверьте кабинет SexySMM. Эта сверка не отправляет покупку
          повторно.
        </Banner>
        <fieldset
          :disabled="busy || Boolean(uncertain)"
          class="border-0 p-0 m-0 flex flex-col gap-4 min-w-0"
        >
          <Input
            v-model="resolution.number"
            label="Номер заказа из кабинета SexySMM"
            inputmode="numeric"
          />
          <label class="flex gap-2 items-start text-sm text-n-slate-12"
            ><input v-model="resolution.absent" type="checkbox" />Проверено в
            кабинете: заказ не создан</label
          >
          <TextArea
            :id="`smm-resolution-${group.id}`"
            v-model="resolution.reason"
            label="Результат проверки / причина"
          />
        </fieldset>
      </template>
      <h3 class="text-sm font-medium text-n-slate-12 m-0">
        Последние заказы группы
      </h3>
      <p v-if="!data.purchases.length" class="text-sm text-n-slate-11 m-0">
        Через эту форму заказы пока не оформлялись.
      </p>
      <article
        v-for="row in data.purchases"
        :key="row.id"
        class="border border-n-weak rounded-xl p-3 min-w-0 flex flex-col gap-2"
      >
        <p class="text-sm text-n-slate-12 m-0 break-words">
          {{
            row.provider_order_id
              ? `№${row.provider_order_id}`
              : `Заявка ${row.id}`
          }}
          · {{ row.service_name }} · {{ row.quantity }} шт.
        </p>
        <p class="text-sm text-n-slate-11 m-0">
          {{ states[row.state]
          }}<span v-if="row.error_code">
            · {{ messages[row.error_code] || 'Требуется проверка' }}</span
          >
        </p>
        <Button
          v-if="row.state === 'unknown'"
          label="Сверить с кабинетом"
          slate
          faded
          :disabled="busy || Boolean(uncertain)"
          @click="beginResolution(row)"
        />
        <Button
          v-if="['checking', 'sending'].includes(row.state)"
          label="Проверить состояние"
          slate
          ghost
          :disabled="busy"
          @click="
            purchaseId = row.id;
            checkStatus();
          "
        />
        <SmmProgress v-if="row.metrics" :metrics="row.metrics" />
      </article>
      <SmmGroupGrowth
        compact
        :rows="data.group_members || []"
        title="Участники группы за текущий месяц"
      />
      <router-link
        :to="{
          name: 'telegram_finance',
          params: { accountId },
          query: { view: 'expense', smm: '1', groups: String(group.id) },
        }"
        class="text-sm text-n-blue-11 underline"
      >
        Расходы и заказы этой группы в учёте
      </router-link>
    </template>
    <template #footer>
      <div
        class="flex flex-col sm:flex-row gap-2 w-full sm:justify-end border-t border-n-weak pt-4"
      >
        <Button label="Закрыть" slate faded @click="dialog?.close()" />
        <Button
          v-if="resolving"
          :label="
            uncertain === 'resolve'
              ? 'Проверить сохранение'
              : 'Сохранить сверку'
          "
          :disabled="busy"
          :is-loading="busy"
          @click="resolve"
        />
        <Button
          v-else-if="uncertain === 'dispatch'"
          label="Проверить состояние заказа"
          :disabled="busy"
          :is-loading="busy"
          @click="checkStatus"
        />
        <Button
          v-else-if="data?.catalogue.configured && !denied"
          :label="
            uncertain === 'quote'
              ? 'Проверить расчёт'
              : quote
                ? 'Оформить платный заказ'
                : 'Рассчитать стоимость'
          "
          :disabled="busy || (Boolean(quote) && !confirmed)"
          :is-loading="busy"
          @click="quote ? dispatch() : calculate()"
        />
      </div>
    </template>
  </Dialog>
</template>
